import {useEffect, useId, useState} from "react";
import {Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useTranslation} from "react-i18next";
import {useUser} from "../../providers/user-context";
import axios from "../../config/axios-config";
import {formatOrderMoney} from "./order-money";

function AmountTotals({totals, t, language}) {
    return <Stack direction="row" useFlexGap flexWrap="wrap" gap={2}>
        {totals.map((total, index) => {
            // Pending orders show the original request explicitly, never an invented confirmed amount.
            const phase = total.confirmed.quantity == null ? "requested" : "confirmed";
            return <Typography key={index} component="span" variant="body2" color="text.secondary">
                {total.currency || t("DistributorCurrencyUnavailable")} · {t(`OrderAmount${phase}`)}: {formatOrderMoney(total[phase].total, total.currency, language)}
            </Typography>;
        })}
    </Stack>;
}

function AmountBreakdown({totals, t, language}) {
    return <>
        <Typography variant="caption" component="p" sx={{mb: 1}}>{t("OrderAmountsPolicy")}</Typography>
        <TableContainer tabIndex={0}><Table size="small" aria-label={t("OrderAmountsTitle")}>
            <TableHead><TableRow>{["Currency", "OrderAmountPhase", "Quantity", "Subtotal", "ShippingCost", "Total"].map(key => <TableCell key={key}>{t(key)}</TableCell>)}</TableRow></TableHead>
            <TableBody>{totals.flatMap((total, index) => [...["requested", "confirmed", "shipped", "delivered", ...["outstanding", "cancelled"].filter(phase => total[phase])].map(phase =>
                <TableRow key={`${index}:${phase}`}><TableCell>{total.currency || t("DistributorCurrencyUnavailable")}</TableCell>
                    <TableCell>{t(`OrderAmount${phase}`)}</TableCell><TableCell>{total[phase].quantity ?? "—"}</TableCell>
                    {["subtotal", "shipping", "total"].map(key => <TableCell key={key}>{formatOrderMoney(total[phase][key], total.currency, language)}</TableCell>)}</TableRow>),
                <TableRow key={`${index}:difference`} sx={{bgcolor: "action.hover"}}><TableCell>{total.currency || "—"}</TableCell>
                    <TableCell>{t("OrderAmountDifference")}</TableCell><TableCell>{total.confirmed.quantity == null || total.requested.quantity == null ? "—" : total.confirmed.quantity - total.requested.quantity}</TableCell>
                    {["subtotal", "shipping", "total"].map(key => <TableCell key={key}>{formatOrderMoney(total.confirmed[key] == null || total.requested[key] == null ? null : Number(total.confirmed[key]) - Number(total.requested[key]), total.currency, language)}</TableCell>)}</TableRow>
            ])}</TableBody>
        </Table></TableContainer>
    </>;
}

export default function OrderAmounts({lineId, active, revision = 0}) {
    const {t, i18n} = useTranslation(), user = useUser();
    const identity = [lineId, active, user.tenantId, user.userIdentity, user.tenantType, user.userRole, i18n.language].join(":");
    const scope = `${identity}:${revision}`, sectionId = useId();
    const [state, setState] = useState({}), [retry, setRetry] = useState(0);
    const [expandedIdentity, setExpandedIdentity] = useState(null);
    useEffect(() => {setExpandedIdentity(null);}, [identity]);
    useEffect(() => {
        if (!active || !lineId) {return;}
        const controller = new AbortController(); setState({scope});
        axios.get(`/api/v1/orders/${lineId}/amounts`, {signal: controller.signal, timeout: 15000,
            headers: {"Language-Context": i18n.language.toUpperCase()}}).then(({data}) => {
            if (!Array.isArray(data?.totals) || data.totals.some(total => ["requested", "confirmed", "shipped", "delivered"].some(phase => !total?.[phase]))) {
                throw new Error("Invalid order amounts");
            }
            if (!controller.signal.aborted) {setState({scope, data});}
        }).catch(() => {if (!controller.signal.aborted) {setState({scope, error: true});}});
        return () => controller.abort();
    }, [scope, active, lineId, i18n.language, retry]);
    const current = state.scope === scope ? state : {};
    if (!active) {return null;}
    return <Box component="section" aria-label={t("OrderAmountsTitle")} sx={{my: 2}}>
        <Accordion variant="outlined" disableGutters expanded={expandedIdentity === identity}
            onChange={(_, expanded) => setExpandedIdentity(expanded ? identity : null)} TransitionProps={{unmountOnExit: true}} sx={{borderRadius: 2, "&::before": {display: "none"}}}>
            <AccordionSummary id={`${sectionId}-summary`} aria-controls={`${sectionId}-details`} expandIcon={<ExpandMoreIcon/>}>
                <Box sx={{minWidth: 0, py: 0.5}}>
                    <Typography variant="h6">{t("OrderAmountsTitle")}</Typography>
                    {current.data && <AmountTotals totals={current.data.totals} t={t} language={i18n.language}/>}
                    {!current.data && !current.error && <Typography role="status" variant="body2">{t("Loading")}</Typography>}
                </Box>
            </AccordionSummary>
            <AccordionDetails>{current.data && <AmountBreakdown totals={current.data.totals} t={t} language={i18n.language}/>}</AccordionDetails>
        </Accordion>
        {current.error && <Alert severity="warning" sx={{mt: 1}} action={<Button onClick={() => setRetry(value => value + 1)}>{t("Retry")}</Button>}>{t("OrderAmountsUnavailable")}</Alert>}
    </Box>;
}
